import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Waves, Fish, Ship, Thermometer, Wind, AlertTriangle,
  TrendingUp, TrendingDown, Droplets, Navigation
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarineForecast({ lat, lon, isDarkMode, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarineData();
  }, [lat, lon]);

  const fetchMarineData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/marine`, {
        params: { lat, lon }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch marine data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'space-y-3' : 'glass rounded-2xl p-6'} animate-pulse`} data-testid={compact ? 'marine-forecast-compact-loading' : 'marine-forecast-loading'}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
            <Waves className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div>
            <h3 className="font-bold">Lake Forecast</h3>
            <div className="h-3 bg-white/10 rounded w-32 mt-1"></div>
          </div>
        </div>
        <div className={compact ? 'grid grid-cols-2 gap-2' : ''}>
          <div className={compact ? 'h-24 bg-white/10 rounded-xl' : 'h-48 bg-white/10 rounded'}></div>
          {compact && <div className="h-24 bg-white/10 rounded-xl"></div>}
        </div>
        {compact && <div className="grid grid-cols-3 gap-2"><div className="h-14 bg-white/10 rounded-xl" /><div className="h-14 bg-white/10 rounded-xl" /><div className="h-14 bg-white/10 rounded-xl" /></div>}
        {compact && <div className="grid grid-cols-2 gap-2" data-testid="marine-compact-details-grid"><div className="h-24 bg-white/10 rounded-xl" /><div className="h-24 bg-white/10 rounded-xl" /></div>}
        {compact && <div className="h-20 bg-white/10 rounded-xl" data-testid="marine-compact-fishing-factors" />}
      </div>
    );
  }

  if (!data?.success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`glass rounded-2xl p-6 ${isDarkMode ? 'bg-blue-500/5' : 'bg-blue-50'}`}
      >
        <div className="flex items-center gap-3 mb-4">
          <Waves className="w-6 h-6 text-blue-400" />
          <h3 className="font-bold">Lake Forecast</h3>
        </div>
        <p className="text-sm opacity-60">
          Unable to load water conditions. Please try refreshing.
        </p>
      </motion.div>
    );
  }

  const getRatingColor = (rating) => {
    if (rating === "Excellent" || rating === "Very Good") return "text-green-400";
    if (rating === "Good") return "text-emerald-400";
    if (rating === "Fair") return "text-yellow-400";
    return "text-red-400";
  };

  // ===== INLAND WATER WIDGET =====
  if (data?.is_inland_water) {
    const { lake_conditions: lc, water, conditions } = data;
    
    if (compact) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
          data-testid="marine-forecast-widget-compact"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <Waves className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold">Lake Forecast</h3>
                <p className="text-xs opacity-60 truncate">Inland water conditions</p>
              </div>
            </div>
            <div className="text-[10px] px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20" data-testid="marine-compact-source-badge">
              USGS + OWM
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2" data-testid="marine-compact-primary-stats">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <p className="text-xs opacity-60">Surface</p>
              <p className={`text-lg font-black ${lc?.surface === 'Glass' || lc?.surface === 'Calm' ? 'text-green-400' : lc?.surface === 'Slight Ripple' || lc?.surface === 'Light Chop' ? 'text-yellow-400' : 'text-red-400'}`}>{lc?.surface}</p>
              <p className="text-[11px] opacity-50">~{lc?.estimated_wave_inches}&quot; chop</p>
            </div>
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <p className="text-xs opacity-60">Wind</p>
              <p className="text-lg font-black">{lc?.wind_speed_mph} mph</p>
              <p className="text-[11px] opacity-50">{lc?.wind_direction}{lc?.wind_gust_mph > 0 ? ` · gust ${lc.wind_gust_mph}` : ''}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2" data-testid="marine-compact-activity-stats">
            <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <Ship className="w-4 h-4 mx-auto mb-1 text-blue-400" />
              <p className="text-[11px] opacity-60">Boating</p>
              <p className={`text-sm font-bold ${getRatingColor(conditions?.boating?.rating)}`}>{conditions?.boating?.index}/10</p>
            </div>
            <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <Fish className="w-4 h-4 mx-auto mb-1 text-emerald-400" />
              <p className="text-[11px] opacity-60">Fishing</p>
              <p className={`text-sm font-bold ${getRatingColor(conditions?.fishing?.rating)}`}>{conditions?.fishing?.index}/10</p>
            </div>
            <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <Thermometer className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
              <p className="text-[11px] opacity-60">Water</p>
              <p className="text-sm font-bold">{water?.temperature_f ? `${water.temperature_f}°F` : '—'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="marine-compact-details-grid">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="marine-compact-water-context">
              <p className="text-xs font-semibold mb-2 text-blue-400">Water context</p>
              <div className="space-y-1 text-xs opacity-70">
                <div className="flex justify-between gap-2"><span>Air temp</span><span className="font-semibold">{lc?.air_temp_f}°F</span></div>
                <div className="flex justify-between gap-2"><span>Visibility</span><span className="font-semibold">{lc?.visibility_mi} mi</span></div>
                <div className="flex justify-between gap-2"><span>Sensor source</span><span className="font-semibold">{water?.source || 'USGS'}</span></div>
              </div>
            </div>
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="marine-compact-guidance">
              <p className="text-xs font-semibold mb-2 text-cyan-400">Lake guidance</p>
              <p className="text-xs opacity-70 leading-relaxed">
                {conditions?.boating?.warnings?.length > 0
                  ? conditions.boating.warnings.slice(0, 2).join(' · ')
                  : lc?.wind_speed_mph > 12
                    ? 'Expect light chop near open points and wind-exposed banks.'
                    : 'Calmer water favors protected coves, docks, and early/late activity.'}
              </p>
            </div>
          </div>

          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`} data-testid="marine-compact-fishing-factors">
            <p className="text-xs font-semibold mb-2 text-blue-500">Fishing factors</p>
            <div className="flex flex-wrap gap-2">
              {(conditions?.fishing?.factors?.length ? conditions.fishing.factors : ['Wind read', 'Surface texture', 'Pressure trend']).slice(0, 4).map((factor) => (
                <span key={factor} className={`text-[11px] px-2 py-1 rounded-lg ${isDarkMode ? 'bg-white/10 text-blue-100' : 'bg-white text-blue-800'}`} data-testid={`marine-factor-${factor.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{factor}</span>
              ))}
            </div>
          </div>
        </motion.div>
      );
    }
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-6"
        data-testid="marine-forecast-widget"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Waves className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <h3 className="font-bold">Lake Forecast</h3>
              <p className="text-sm opacity-60">Inland water conditions</p>
            </div>
          </div>
          {conditions?.boating?.advisory && (
            <div className="flex items-center gap-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium px-2 py-1 rounded-full">
              <AlertTriangle className="w-3 h-3" />
              Advisory
            </div>
          )}
          <div className="text-[10px] px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20" data-testid="marine-source-comparison-badge">
            USGS + OWM water cross-check
          </div>
        </div>

        {/* Lake Conditions + Wind */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Waves className="w-4 h-4 text-blue-400" />
              Lake Surface
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm opacity-70">Conditions</span>
                <span className={`font-medium ${
                  lc?.surface === 'Glass' || lc?.surface === 'Calm' ? 'text-green-400' :
                  lc?.surface === 'Slight Ripple' || lc?.surface === 'Light Chop' ? 'text-yellow-400' : 'text-red-400'
                }`}>{lc?.surface}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm opacity-70">Est. Waves</span>
                <span className="font-medium">~{lc?.estimated_wave_inches}&quot; high</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm opacity-70">Wind</span>
                <span className="font-medium">{lc?.wind_speed_mph} mph {lc?.wind_direction}</span>
              </div>
              {lc?.wind_gust_mph > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm opacity-70">Gusts</span>
                  <span className="font-medium">{lc.wind_gust_mph} mph</span>
                </div>
              )}
            </div>
          </div>

          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-cyan-400" />
              Water Data
            </h4>
            <div className="space-y-2">
              {water?.temperature_f ? (
                <div className="flex justify-between">
                  <span className="text-sm opacity-70">Water Temp</span>
                  <span className="font-medium">{water.temperature_f}°F</span>
                </div>
              ) : (
                <div className="flex justify-between">
                  <span className="text-sm opacity-70">Water Temp</span>
                  <span className="text-sm opacity-50">No sensor nearby</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm opacity-70">Air Temp</span>
                <span className="font-medium">{lc?.air_temp_f}°F</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm opacity-70">Visibility</span>
                <span className="font-medium">{lc?.visibility_mi} mi</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm opacity-70">Source</span>
                <span className="text-xs opacity-50">{water?.source || "USGS"}</span>
              </div>
              <div className="text-[10px] opacity-50" data-testid="marine-source-comparison-note">
                Water sensors are paired with live wind/visibility to rate boating and fishing confidence.
              </div>
            </div>
          </div>
        </div>

        {/* Activity Conditions */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <Ship className="w-6 h-6 mx-auto mb-2 text-blue-400" />
            <p className="text-xs opacity-60 mb-1">Boating</p>
            <p className={`font-bold ${getRatingColor(conditions?.boating?.rating)}`}>
              {conditions?.boating?.rating}
            </p>
            <p className="text-xs opacity-50">{conditions?.boating?.index}/10</p>
          </div>
          <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <Fish className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
            <p className="text-xs opacity-60 mb-1">Fishing</p>
            <p className={`font-bold ${getRatingColor(conditions?.fishing?.rating)}`}>
              {conditions?.fishing?.rating}
            </p>
            <p className="text-xs opacity-50">{conditions?.fishing?.index}/10</p>
          </div>
        </div>

        {/* Fishing Factors */}
        {conditions?.fishing?.factors?.length > 0 && (
          <div className={`mt-4 p-3 rounded-xl ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
            <p className="text-xs font-medium text-emerald-500 mb-1">Fishing Conditions</p>
            <p className="text-sm opacity-70">{conditions.fishing.factors.join(" · ")}</p>
          </div>
        )}
      </motion.div>
    );
  }

  // ===== COASTAL / OCEAN WIDGET =====
  const { tides, waves, water, conditions } = data;

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
        data-testid="marine-forecast-widget-compact"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Waves className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold">Lake Forecast</h3>
              <p className="text-xs opacity-60 truncate">Tides, waves & conditions</p>
            </div>
          </div>
          <div className="text-[10px] px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20" data-testid="marine-compact-source-badge">
            NOAA + OWM
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2" data-testid="marine-compact-primary-stats">
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-xs opacity-60">Waves</p>
            <p className="text-lg font-black">{waves?.height_ft} ft</p>
            <p className="text-[11px] opacity-50">{waves?.conditions} · {waves?.direction}</p>
          </div>
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-xs opacity-60">Water</p>
            <p className="text-lg font-black">{water?.temperature_f}°F</p>
            <p className="text-[11px] opacity-50">{water?.visibility}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="marine-compact-activity-stats">
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <Ship className="w-4 h-4 mx-auto mb-1 text-blue-400" />
            <p className="text-[11px] opacity-60">Boating</p>
            <p className={`text-sm font-bold ${getRatingColor(conditions?.boating?.rating)}`}>{conditions?.boating?.index}/10</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <Fish className="w-4 h-4 mx-auto mb-1 text-emerald-400" />
            <p className="text-[11px] opacity-60">Fishing</p>
            <p className={`text-sm font-bold ${getRatingColor(conditions?.fishing?.rating)}`}>{conditions?.fishing?.index}/10</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <Waves className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
            <p className="text-[11px] opacity-60">Swim</p>
            <p className={`text-sm font-bold ${conditions?.swimming?.safe ? 'text-green-400' : 'text-red-400'}`}>{conditions?.swimming?.safe ? 'Safe' : 'Caution'}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6"
      data-testid="marine-forecast-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
            <Waves className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div>
              <h3 className="font-bold">Lake Forecast</h3>
            <p className="text-sm opacity-60">Tides, waves & water conditions</p>
          </div>
        </div>
        
        {conditions?.boating?.advisory && (
          <div className="flex items-center gap-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium px-2 py-1 rounded-full">
            <AlertTriangle className="w-3 h-3" />
            {conditions.boating.advisory}
          </div>
        )}
        <div className="text-[10px] px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20" data-testid="marine-source-comparison-badge">
          NOAA tides + OWM weather cross-check
        </div>
      </div>

      {/* Tide Schedule */}
      {tides?.today?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 opacity-70">Today&apos;s Tides</h4>
          <div className="grid grid-cols-4 gap-2">
            {tides.today.map((tide, idx) => (
              <div 
                key={`item-${idx}`}
                className={`p-3 rounded-xl text-center ${
                  tide.type === 'High' 
                    ? isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                    : isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'
                }`}
              >
                {tide.type === 'High' ? (
                  <TrendingUp className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                ) : (
                  <TrendingDown className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
                )}
                <p className="text-xs opacity-60">{tide.type}</p>
                <p className="font-bold">{tide.height_ft} ft</p>
                <p className="text-xs opacity-70">{tide.time}</p>
              </div>
            ))}
          </div>
          <p className="text-xs opacity-50 mt-2 text-center">
            Tidal Range: {tides.tidal_range} · Type: {tides.tide_type}
          </p>
        </div>
      )}

      {/* Wave & Water Conditions */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Waves className="w-4 h-4 text-blue-400" />
            Wave Conditions
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Height</span>
              <span className="font-medium">{waves?.height_ft} ft ({waves?.height_m} m)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Period</span>
              <span className="font-medium">{waves?.period_seconds} sec</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Direction</span>
              <span className="font-medium flex items-center gap-1">
                <Navigation className="w-3 h-3" />
                {waves?.direction}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Conditions</span>
              <span className={`font-medium ${
                waves?.conditions === 'Calm' ? 'text-green-400' : 
                waves?.conditions === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
              }`}>{waves?.conditions}</span>
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Droplets className="w-4 h-4 text-cyan-400" />
            Water Conditions
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Temperature</span>
              <span className="font-medium">{water?.temperature_f}°F ({water?.temperature_c}°C)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Visibility</span>
              <span className="font-medium">{water?.visibility}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Salinity</span>
              <span className="font-medium">{water?.salinity}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Conditions */}
      <div className="grid grid-cols-3 gap-3">
        <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Ship className="w-6 h-6 mx-auto mb-2 text-blue-400" />
          <p className="text-xs opacity-60 mb-1">Boating</p>
          <p className={`font-bold ${getRatingColor(conditions?.boating?.rating)}`}>
            {conditions?.boating?.rating}
          </p>
          <p className="text-xs opacity-50">{conditions?.boating?.index}/10</p>
        </div>
        <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Fish className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
          <p className="text-xs opacity-60 mb-1">Fishing</p>
          <p className={`font-bold ${getRatingColor(conditions?.fishing?.rating)}`}>
            {conditions?.fishing?.rating}
          </p>
          <p className="text-xs opacity-50">{conditions?.fishing?.index}/10</p>
        </div>
        <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Waves className="w-6 h-6 mx-auto mb-2 text-cyan-400" />
          <p className="text-xs opacity-60 mb-1">Swimming</p>
          <p className={`font-bold ${conditions?.swimming?.safe ? 'text-green-400' : 'text-red-400'}`}>
            {conditions?.swimming?.safe ? "Safe" : "Caution"}
          </p>
          <p className="text-xs opacity-50">Rip: {conditions?.swimming?.rip_current_risk}</p>
        </div>
      </div>

      {conditions?.fishing?.factors?.length > 0 && (
        <div className={`mt-4 p-3 rounded-xl ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
          <p className="text-xs font-medium text-emerald-500 mb-1">Fishing Conditions</p>
          <p className="text-sm opacity-70">{conditions.fishing.factors.join(" · ")}</p>
        </div>
      )}
    </motion.div>
  );
}
